import { CSSProperties } from "react";
import { ListChildComponentProps } from "react-window";

import { ArrayHelper } from "@/Common/Helpers/ArrayHelper";
import { ICheckable } from "@/Common/Models/UI/Inputs/ICheckable";
import { CheckboxControl } from "./CheckboxControl";
import { LazyRenderingFixedList } from "./LazyRenderingFixedList";

interface ICheckedListControlProps {
    dataCy?: string;
    DataSource: Immutable.List<ICheckable>;
    MaxTextLength?: number;
    Operation?: number;
    onChange?: (isChecked: boolean, CheckboxId: string, target?: number) => void;

    limitTextWidth?: boolean;
    sort?: boolean;
    labelStyle?: any;
    idModifier?: string;
    resolveValueInStringChange?: boolean; // if this set to true: object name will be used to find it's value in cacheStore.GetString()
    disabled?: boolean;
    style?: React.CSSProperties;

    /**
     * The height of the lazy rendering container.
     *
     * @default 400
     */
    lazyRenderingContainerHeight?: number;

    /**
     * The width of the lazy rendering container.
     *
     * @default '100%' (100% of the parent container's width)
     */
    lazyRenderingContainerWidth?: number;

    /**
     * Whether to enable lazy rendering (only render visible items).
     * Note that it does enforce fix height and truncation on text.
     */
    enableLazyRendering?: boolean;
}

class CheckedListControlState {}

class CheckedListControl extends React.Component<ICheckedListControlProps, CheckedListControlState> {
    private readonly fixedListDefaultHeight = 400;
    private readonly fixedListDefaultWidth = "100%";
    private readonly fixedLisItemDefaultHeight = 28;

    public render(): JSX.Element {
        var maxLen: number = undefined;
        var self = this;
        if (this.props.MaxTextLength) {
            maxLen = this.props.MaxTextLength;
        }

        var style = null;

        var css = null;
        if (this.props.limitTextWidth === true) {
            css = "text-ellipsis";
            style = { top: "-3px" };
        }

        let sortedArray = this.props.DataSource.toArray();
        sortedArray = this.props.sort !== false ? ArrayHelper.SortDynamic(sortedArray, "Name") : sortedArray;

        var labelStyle = this.props.labelStyle;
        const visibleItems = sortedArray.filter((d: ICheckable) => d.Visible === true);
        const isLazyRenderingEnabled = cacheStore.ClinicSettings.EnableLazyRenderingFilterSettingsClinicians && this.props.enableLazyRendering;

        return (
            <div className={css} style={this.props.style || {}}>
                {isLazyRenderingEnabled && (
                    <LazyRenderingFixedList
                        height={this.props.lazyRenderingContainerHeight ?? this.fixedListDefaultHeight}
                        items={visibleItems}
                        itemSize={this.fixedLisItemDefaultHeight}
                        width={this.props.lazyRenderingContainerWidth ?? this.fixedListDefaultWidth}
                        row={childProps => this.renderRow(childProps, maxLen, style, labelStyle)}
                    />
                )}
                {!isLazyRenderingEnabled && visibleItems.map((o: ICheckable) => {
                    // somewhat of a hack for when we have two elements with same ID as a checkbox on the same page, to avoid cross interactions
                    var id = (self.props && self.props.idModifier) ? o.Id + self.props.idModifier : o.Id;

                    var n = self.props.resolveValueInStringChange === true ? cacheStore.GetString(o.Name) : o.Name;

                    if (maxLen && maxLen > 0) {
                        return (
                            <div key={id} data-cy={this.props.dataCy}>
                                <CheckboxControl
                                    idModifier={self.props.idModifier}
                                    id={id}
                                    checked={o.Checked}
                                    label={n}
                                    target={o.Target}
                                    maxLen={maxLen}
                                    operation={self.props.Operation}
                                    onChange={self.props.onChange}
                                    style={style}
                                    labelStyle={labelStyle}
                                    disabled={self.props.disabled}
                                    title={o.Title}
                                    dataCy={o.DataCy}
                                />
                            </div>
                        );
                    }
                    else {
                        return (
                            <div key={id} data-cy={this.props.dataCy}>
                                <CheckboxControl
                                    idModifier={self.props.idModifier}
                                    id={id}
                                    checked={o.Checked}
                                    label={n}
                                    onChange={self.props.onChange}
                                    target={o.Target}
                                    operation={self.props.Operation}
                                    style={style}
                                    labelStyle={labelStyle}
                                    disabled={self.props.disabled}
                                    title={o.Title}
                                    dataCy={o.DataCy}
                                />
                            </div>
                        );
                    }
                })}
            </div>
        );
    }

    private renderRow(
        { data, index, style }: ListChildComponentProps<ReadonlyArray<ICheckable>>,
        maxLen?: number,
        customStyle?: CSSProperties,
        labelStyle?: CSSProperties
    ): JSX.Element {
        const item = data[index];
        const id = this.props?.idModifier ? item.Id + this.props.idModifier : item.Id;
        const label = this.props.resolveValueInStringChange === true ? cacheStore.GetString(item.Name) : item.Name;

        return (
            <div style={style}>
                <div data-cy={this.props.dataCy}>
                    <CheckboxControl
                        idModifier={this.props.idModifier}
                        id={id}
                        checked={item.Checked}
                        label={label}
                        target={item.Target}
                        maxLen={maxLen}
                        operation={this.props.Operation}
                        onChange={this.props.onChange}
                        style={customStyle}
                        labelStyle={{ ...labelStyle }}
                        disabled={this.props.disabled}
                        title={item.Title}
                        truncateText
                    />
                </div>
            </div>
        );
    }
}

export { CheckedListControl, ICheckedListControlProps };
